import { useMutation, useQuery } from 'react-query'
import {
  GET_SECTION_COMMENT_TYPE,
  getInspectionsAdvance,
  getSectionCommentListService,
  postApproveSampleApi,
  postRequestRevisionApi,
  REQUEST_REVISION_PAYLOAD_TYPE,
  postInspectionComment,
  getInspectionAdvanceSummary,
  getPropertiesByFields,
} from './Inspection.service'
import {
  DEFAULT_OVERALL_RESULT,
  INSPECTION_PARAMS_TYPE,
  INSPECTION_PAYLOAD_TYPE,
  QSF_QUERY_KEY,
} from '@/constant/qsf'
import { get, includes, isEmpty, join, map, omitBy, sortBy } from 'lodash'
import moment from 'moment'
import { INSPECTION_TEXT, InspectionConstant } from './Inspection.constant'
import { PAGINATION } from '@/constant/common'
import { queryClient } from '@/config/react-query'
import { COMPANY_SETTING_OVERALL_RESULT } from '@/constant/user'
import { getCompanySetting } from '@/screens/SystemConfigure/UserManagment/UserManagment.transform'

const formatInspectionPayload = (payload: INSPECTION_PAYLOAD_TYPE) => {
  const { submitted_date, planned_date } = payload
  const getSubmitDate = map(submitted_date, (dateJs) =>
    moment(dateJs).format(INSPECTION_TEXT.dateFomat)
  )
  const getPlannedDate = map(planned_date, (dateJs) =>
    moment(dateJs).format(INSPECTION_TEXT.dateFomat)
  )

  return omitBy(
    {
      ...payload,
      planned_date: join(getPlannedDate, ','),
      submitted_date: join(getSubmitDate, ','),
    },
    isEmpty
  )
}

export const useInspectionHook = (
  params: INSPECTION_PARAMS_TYPE,
  payload: INSPECTION_PAYLOAD_TYPE
) => {
  const result = useQuery(
    ['INSPECTIONS', params, payload],
    async () => {
      const wrapPayload = formatInspectionPayload(payload)

      const getGroup = includes(['ALL'], params.group)
        ? {}
        : { group: params.group }

      const wrapParams = {
        page_number: params?.page_number?.toString(),

        page_size: params?.page_size?.toString() || `${PAGINATION.page_size}`,
        ...getGroup,
      }

      const toQueryParam = new URLSearchParams(wrapParams).toString()
      const tmp = await getInspectionsAdvance(toQueryParam, wrapPayload)

      return tmp?.data
    },
    {
      getNextPageParam: (lastPage) => {
        const nextPage = lastPage.data?.pagination.next_page
        const totalPage = lastPage.data?.pagination.total_page
        return nextPage <= totalPage ? nextPage : false
      },
      keepPreviousData: true,
    }
  )
  return result
}

export const useInspectionAddCommentHook = () => {
  const result = useMutation(
    async (params: any) => await postInspectionComment(params)
  )

  return result
}

export const useGetSectionCommentListHook = (
  params: GET_SECTION_COMMENT_TYPE
) => {
  const result = useQuery({
    queryKey: ['QUERY_SECTION_COMMENTS', params],
    queryFn: async () => await getSectionCommentListService(params),
  })

  return result
}

export const useRequestRevisionsHook = () => {
  const result = useMutation(
    async (params: REQUEST_REVISION_PAYLOAD_TYPE) =>
      await postRequestRevisionApi(params)
  )

  return result
}

export const useApproveSamplesHook = () => {
  const result = useMutation(
    async (params: any) => await postApproveSampleApi(params)
  )

  return result
}

export const useInspectionSummary = (
  payload: INSPECTION_PAYLOAD_TYPE = {},
  options?: any
) => {
  const api = async () => {
    const wrapPayload = formatInspectionPayload(payload)
    const resultSummary = await getInspectionAdvanceSummary(wrapPayload)

    return resultSummary
  }

  return useQuery(
    [InspectionConstant.QUERY_KEY.INSPECTIONS_ADVANCE_SUMMARY, payload],
    api,
    {
      keepPreviousData: true,
      onSuccess: (data) => {
        if (data) {
          queryClient.setQueryData(
            [InspectionConstant.QUERY_KEY.INSPECTION_SUMMARY],
            data
          )
        }
      },
      ...options,
    }
  )
}

export const useGetPropertiesHook = (fields: string[]) => {
  return useQuery({
    queryKey: [InspectionConstant.QUERY_KEY.GET_PROPERTIES_FIELDS, fields],
    queryFn: async () => {
      const result = await getPropertiesByFields(fields)
      return result
    },
    enabled: Array.isArray(fields) && fields.length > 0,
    onError(err) {
      console.error('USE GET PROPERTIES', err)
    },
  })
}

export const useOverallResultSettingHook = () => {
  return useQuery(
    [QSF_QUERY_KEY.GET_COMPANY_SETTING_INSPECTION_OVERALL_RESULT],
    async () => {
      const resp = await getCompanySetting('', COMPANY_SETTING_OVERALL_RESULT)
      const types: any[] = get(resp, 'value.types', [])
      const customType = types.find((t: any) => t.value === 'custom') ?? {}

      const rawOptions: any[] = get(customType, 'options', [])
      const safeOptions =
        rawOptions.length > 0 ? rawOptions : DEFAULT_OVERALL_RESULT

      const systemSetting: any[] = sortBy(
        safeOptions,
        (opt: any) => opt.display_order_web
      )
      // TODO: fetch company setting override when API is ready
      // const companySetting: any[] = []
      return {
        types,
        options: systemSetting,
        isSystemEmpty: rawOptions.length === 0,
      }
    }
  )
}
